import type React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import clone from 'lodash/clone';
import {Concept} from 'types';
import type {
  CurrentFilterType,
  CustomFilterType,
  Filter,
  GeneratedFilterType,
} from 'services/advancedSearch/types';
import {
  excludeBokbasenFilter,
  excludeInternationalFilter,
} from 'services/advancedSearch';
import {useClearAdvancedSearchItems} from 'services/advancedSearchItems';
import {selectAdvancedSearchItems} from 'services/advancedSearchItems/reducer';
import {useSetAdvancedSearchViewMode} from 'services/advancedSearchViewMode';
import {
  advancedSearchFiltersUpdated,
  selectSettingsAdvancedSearchCurrentFilters,
} from 'services/settings';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import type {CurrentFiltersType} from '../types';
import {
  assertAsCustomFilterType,
  isNotCustomFilter,
} from '../functions/assertAsCustomFilterType';

export const useCurrentFilters = (): CurrentFiltersType => {
  const dispatch = useAppDispatch();
  const currentFilters = useAppSelector(
    selectSettingsAdvancedSearchCurrentFilters,
  );
  const advancedSearchItems = useAppSelector(selectAdvancedSearchItems);
  const clearAdvancedSearchItems = useClearAdvancedSearchItems();
  const setAdvancedSearchViewMode = useSetAdvancedSearchViewMode();

  const setCurrentFilters = useCallback(
    (newFilters: React.SetStateAction<CurrentFilterType[]>) => {
      const filters = !Array.isArray(newFilters)
        ? newFilters(currentFilters)
        : newFilters;

      dispatch(advancedSearchFiltersUpdated(filters));
    },
    [currentFilters, dispatch],
  );

  const handleAddFilter = useCallback(
    (filter: Filter | CustomFilterType) => {
      const customFilter = assertAsCustomFilterType(filter);

      const currentMax = Math.max(
        ...currentFilters.filter(isNotCustomFilter).map(o => o.id),
      );
      const nextId = Number.isFinite(currentMax) ? currentMax + 1 : 1;

      const id = customFilter ? customFilter.id : nextId;

      setCurrentFilters(prevState => [
        ...prevState,
        {
          ...filter,
          id,
        },
      ]);
    },
    [currentFilters, setCurrentFilters],
  );

  const handleUpdateFilter = useCallback(
    (id: number, filter: Filter) => {
      setCurrentFilters(prevState => {
        const i = prevState.findIndex(f => f.id === id);
        const nextState = clone(prevState);

        nextState[i] = {
          ...filter,
          id,
        };
        return nextState;
      });
    },
    [setCurrentFilters],
  );

  const handleRemoveById = useCallback(
    (id: number) => {
      setCurrentFilters(prevState => {
        const i = prevState.findIndex(f => f.id === id);
        const nextState = clone(prevState);
        nextState.splice(i, 1);
        return nextState;
      });
    },
    [setCurrentFilters],
  );

  const handleSetCurrentFilters = useCallback(
    (filters: Array<Filter | CustomFilterType>) => {
      const currentFilters: CurrentFilterType[] = [];
      filters.forEach((f, i) => {
        const customFilter = assertAsCustomFilterType(f);
        currentFilters.push({
          ...f,
          id: customFilter ? customFilter.id : i,
        });
      });
      setCurrentFilters(currentFilters);
    },
    [setCurrentFilters],
  );

  useEffect(() => {
    if (advancedSearchItems.itemIds.length && advancedSearchItems.type) {
      const advancedSearchItemsFilter: GeneratedFilterType = {
        id: 2001,
        operation: 'EQUAL',
        values: advancedSearchItems.itemIds,
        name:
          advancedSearchItems.type === Concept.manifestation
            ? 'manifestationId'
            : 'workId',
        generated: true,
      };

      /*
      If operation is NULL or NOT_NULL, no values is needed to filter. So we
      have to change the operation to something that takes no effect when no
      value is set.
       */
      const resetValues = (c: CurrentFilterType): CurrentFilterType => {
        if (c.operation === 'NULL' || c.operation === 'NOT_NULL') {
          return {
            ...c,
            operation: 'EQUAL',
            values: [],
          };
        }

        return {
          ...c,
          values: [],
        };
      };

      const newFilters: CurrentFilterType[] = [
        advancedSearchItemsFilter,
        ...currentFilters
          // Remove hidden
          .filter(c => !c.hidden)
          .filter(c => !c.generated)
          // Reset values on non-advanced search items.
          .map(resetValues),
      ];
      setAdvancedSearchViewMode(advancedSearchItems.type);
      clearAdvancedSearchItems();
      setCurrentFilters(newFilters);
    } else {
      const invalidIds = currentFilters.some(
        c =>
          !Number.isInteger(c.id) ||
          c.id < 0 ||
          !Number.isFinite(c.id) ||
          Number.isNaN(c.id),
      );
      const hasDuplicates = currentFilters.some(c => {
        return currentFilters.filter(cc => cc.id === c.id).length > 1;
      });

      // Reset if any lines are invalid.
      if (invalidIds || hasDuplicates) {
        setCurrentFilters([excludeInternationalFilter, excludeBokbasenFilter]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    return {
      currentFilters,
      addCurrentFilter: handleAddFilter,
      setCurrentFilters: handleSetCurrentFilters,
      updateCurrentFilter: handleUpdateFilter,
      removeCurrentFilterById: handleRemoveById,
    };
  }, [
    currentFilters,
    handleAddFilter,
    handleRemoveById,
    handleSetCurrentFilters,
    handleUpdateFilter,
  ]);
};
